import { Component } from '@theme/component';

/** Infinite, always-left slider for the announcement bar */
export class AnnouncementBar extends Component {
  #track;              // flex track we create
  #slides = [];        // [cloned last][...originals][cloned first]
  #count = 0;          // number of ORIGINAL slides
  #index = 1;          // current index in #slides (start at first REAL slide)
  #timer = null;
  #animating = false;
  #transitionMs = 720; // keep in sync with CSS if you change it

  connectedCallback() {
    super.connectedCallback();

    const wrap = this.querySelector('.announcement-bar__slides');
    if (!wrap) return;

    // Grab originals from current markup
    const originals = Array.from(this.querySelectorAll('.announcement-bar__slide'));
    this.#count = originals.length;
    if (this.#count === 0) return;

    // If only one slide, no slider needed
    if (this.#count === 1) {
      originals[0].setAttribute('aria-hidden', 'false');
      return;
    }

    // Build track once
    if (!this.#track) {
      this.#track = document.createElement('div');
      this.#track.className = 'annc-track';

      const headClone = originals[this.#count - 1].cloneNode(true); // last -> head
      const tailClone = originals[0].cloneNode(true);               // first -> tail
      headClone.classList.add('annc-clone', 'annc-slide');
      tailClone.classList.add('annc-clone', 'annc-slide');
      headClone.setAttribute('aria-hidden', 'true');
      tailClone.setAttribute('aria-hidden', 'true');

      // Move originals into track and normalize
      const normalize = (el, i) => {
        el.classList.add('annc-slide');
        el.setAttribute('role', 'group');
        el.setAttribute('aria-roledescription', 'announcement');
        el.setAttribute('aria-label', `${i + 1} of ${this.#count}`);
        return el;
      };

      wrap.innerHTML = '';
      wrap.appendChild(this.#track);
      this.#track.appendChild(headClone);
      originals.forEach((el, i) => this.#track.appendChild(normalize(el, i)));
      this.#track.appendChild(tailClone);

      this.#slides = Array.from(this.#track.children);

      // Position at first REAL slide (index 1) without animation
      this.#jumpTo(1);

      // Set initial ARIA (show first REAL, hide the others)
      const originalsOnly = this.#slides.filter(el => !el.classList.contains('annc-clone'));
      originalsOnly.forEach((el, i) => el.setAttribute('aria-hidden', `${i !== 0}`));

      // Handle wrap (only react to the track's transform transition)
      this.#track.addEventListener('transitionend', (e) => {
        if (e.target !== this.#track || e.propertyName !== 'transform') return;
        this.#onEnd();
      });

      // <-- IMPORTANT: marks ready so CSS switches from stacked to slider layout
      this.classList.add('annc-ready');
    }

    // Wire arrows (existing markup)
    this.querySelector('.slideshow-control--previous')
      ?.addEventListener('click', (ev) => { ev.preventDefault(); this.previous(); });
    this.querySelector('.slideshow-control--next')
      ?.addEventListener('click', (ev) => { ev.preventDefault(); this.next(); });

    // Pause/resume behaviours
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.resume);
    document.addEventListener('visibilitychange', () => document.hidden ? this.pause() : this.resume());

    // Start autoplay (uses autoplay="<seconds>")
    this.play();
  }

  // Always advance left
  next() {
    if (this.#animating) return;
    this.#animating = true;
    this.#index += 1;
    this.#slideTo(this.#index);
  }

  // Go right only when user clicks Previous
  previous() {
    if (this.#animating) return;
    this.#animating = true;
    this.#index -= 1;
    this.#slideTo(this.#index);
  }

  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;
    this.paused = false;
    this.suspend();
    this.#timer = setInterval(() => {
      if (this.#animating || this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() { this.paused = true; this.suspend(); }
  suspend = () => { if (this.#timer) { clearInterval(this.#timer); this.#timer = null; } };
  resume  = () => { if (!this.autoplay || this.paused) return; this.play(); };

  get autoplay() {
    return Boolean(this.autoplayInterval) && this.#count > 1;
  }
  get autoplayInterval() {
    const s = this.getAttribute('autoplay');
    const sec = parseInt(`${s}`, 10);
    return Number.isNaN(sec) ? 5000 : Math.max(2, sec) * 1000;
  }
  get paused() { return this.hasAttribute('paused'); }
  set paused(v) { this.toggleAttribute('paused', v); }

  /* --- internals --- */
  #enableTransition() { this.#track.style.transition = `transform ${this.#transitionMs}ms ease`; }
  #disableTransition() { this.#track.style.transition = 'none'; }
  #slideTo(i) { this.#enableTransition(); this.#track.style.transform = `translateX(${-100 * i}%)`; }
  #jumpTo(i)  { this.#disableTransition(); this.#track.style.transform = `translateX(${-100 * i}%)`; void this.#track.offsetWidth; this.#index = i; }

  #onEnd() {
    const max = this.#count + 1; // tail clone index
    // If we landed on a clone, snap (no animation) to matching real slide
    if (this.#index === 0)        this.#jumpTo(this.#count); // head clone -> last real
    else if (this.#index === max) this.#jumpTo(1);           // tail clone -> first real

    // Update ARIA on ORIGINAL slides (visible real index)
    const realIdx = this.#index === 0 ? this.#count - 1
                  : this.#index === max ? 0
                  : this.#index - 1;
    const originals = this.#slides.filter(el => !el.classList.contains('annc-clone'));
    originals.forEach((el, i) => el.setAttribute('aria-hidden', `${i !== realIdx}`));

    this.#animating = false;
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
